import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { IDashboardCategoryValue } from "../../../../../@types/admin";
import { getFriendlySourceName } from "../../../../../utils/chartSourceUtils";
import {
  CHART_AXIS_STROKE,
  CHART_AXIS_TICK_STYLE,
  CHART_DONUT_COLORS,
} from "./chartConstants";
import { DashboardTooltip } from "./DashboardTooltip";

interface IProps {
  data: IDashboardCategoryValue[];
}

const LOG_SCALE_RATIO_THRESHOLD = 50;

// The HSE doesn't use Fitbit, Dexcom or Google Health, so exclude them from
// the graph. Source codes arrive in arbitrary casing, so match against
// lowercase.
const EXCLUDED_DEVICE_SOURCES = new Set(["fitbit", "dexcom", "google-health"]);

export function DeviceUsageChart({ data }: IProps) {
  const { t } = useTranslation();

  const chartData = data
    .filter((d) => !EXCLUDED_DEVICE_SOURCES.has(d.category.toLowerCase()))
    .map((d) => ({
      name: getFriendlySourceName(d.category, t),
      value: d.value,
    }));

  // A single high-volume source (e.g. dexcom, fitbit) typically dwarfs the
  // others by 100×+; on a linear scale the low-volume sources collapse into
  // invisible slivers. Switch to a log scale only when the max-to-min ratio
  // is genuinely large — otherwise linear is more intuitive.
  const positiveValues = chartData.map((d) => d.value).filter((v) => v > 0);
  const useLogScale =
    positiveValues.length > 1 &&
    Math.max(...positiveValues) / Math.min(...positiveValues) >=
      LOG_SCALE_RATIO_THRESHOLD;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        layout="vertical"
        data={chartData}
        margin={{ top: 8, right: 24, left: 0, bottom: 8 }}
        barCategoryGap={10}
      >
        <XAxis
          type="number"
          stroke={CHART_AXIS_STROKE}
          tick={CHART_AXIS_TICK_STYLE}
          scale={useLogScale ? "log" : "linear"}
          domain={useLogScale ? [1, "auto"] : [0, "auto"]}
          allowDataOverflow={useLogScale}
        />
        <YAxis
          type="category"
          dataKey="name"
          stroke={CHART_AXIS_STROKE}
          tick={CHART_AXIS_TICK_STYLE}
          width={160}
        />
        <Tooltip
          cursor={{ fill: "rgba(117, 98, 168, 0.06)" }}
          content={(p) => (
            <DashboardTooltip
              {...p}
              valueLabel={t("reports_charts_readingsValue")}
            />
          )}
        />
        <Bar dataKey="value" radius={[0, 6, 6, 0]} isAnimationActive={false}>
          {chartData.map((_, idx) => (
            <Cell
              key={`device-${idx}`}
              fill={CHART_DONUT_COLORS[idx % CHART_DONUT_COLORS.length]}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
